import { Provider, Signal, TemplateRef, Type } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Command, ICommand } from '@workbuddy/ui/model';
import { ActivityEntityName } from '@jms/model';
import type { AvatarToken } from '@workbuddy/ui/v2/components/primitives/content';

// ── Mode ────────────────────────────────────────────────────────────

export enum SettingsPanelMode {
  Sidebar = 'sidebar',
  CommandPalette = 'command-palette',
}

// ── Menu item types ─────────────────────────────────────────────────

export type SettingsPanelMenuItem = SettingsPanelNavigateItem | SettingsPanelDropdownItem | SettingsPanelSwitchItem | SettingsPanelInputItem | SettingsPanelActionItem | SettingsPanelSelectItem | SettingsPanelTabsItem | SettingsPanelEntityPickerItem | SettingsPanelIconPickerItem | SettingsPanelTonePickerItem | SettingsPanelCompoundItem | SettingsPanelInlineComponentItem | SettingsPanelDividerItem | SettingsPanelGroupItem;

export type SettingsPanelTone =
  | 'gray' | 'brand'
  | 'indigo' | 'fuchsia' | 'pink' | 'purple'
  | 'orangeDark' | 'blueLight' | 'moss';

export type SettingsPanelVisibility = boolean | ((...args: any[]) => boolean);
export type SettingsPanelErrorMessage = string | Record<string, string> | ((errors: Record<string, unknown>[]) => string | null);

export type SettingsPanelDisplayValue = string | SettingsPanelDisplayPreview;

export interface SettingsPanelDisplayPreview {
  icon?: string;
  tone?: SettingsPanelTone;
  text?: string;
}

export interface SettingsPanelItemBase {
  id: string;
  label: string;
  icon?: string;
  control?: FormControl;
  hidden?: SettingsPanelVisibility;
  disabled?: SettingsPanelVisibility;
  required?: boolean;
  valueAlign?: 'left' | 'right';
  displayValue$?: Signal<SettingsPanelDisplayValue>;
  errorMessage?: SettingsPanelErrorMessage;
}

export interface SettingsPanelNavigateItem extends SettingsPanelItemBase {
  type: 'navigate';
  value?: string;
  count?: number;
  component?: Type<unknown>;
  picker?: SettingsPanelPickerConfig;
  navigateOptions?: SettingsPanelNavigateOptions;
}

export type SettingsPanelOptionId = string | number;

export interface SettingsPanelPickerConfig {
  options: SettingsPanelSelectOption[] | Signal<SettingsPanelSelectOption[]>;
  mode: 'single' | 'multi';
  searchable?: boolean;
  selected?: SettingsPanelOptionId | SettingsPanelOptionId[];
  emptyMessage?: string;
  emptyIcon?: string;
  reorderable?: boolean;
  selectedLabel?: string;
  availableLabel?: string;
  onSearch?: (query: string) => void;
}

export interface SettingsPanelDropdownItem extends SettingsPanelItemBase {
  type: 'dropdown';
  value?: string;
  options: SettingsPanelSelectOption[] | Signal<SettingsPanelSelectOption[]>;
  selected?: SettingsPanelOptionId;
  onSelect?: (selected: SettingsPanelOptionId, option: SettingsPanelSelectOption) => void;
}

export interface SettingsPanelSwitchItem extends SettingsPanelItemBase {
  type: 'switch';
  checked?: boolean;
  onToggle?: (checked: boolean) => void;
}

export interface SettingsPanelInputItem extends SettingsPanelItemBase {
  type: 'input';
  value?: string;
  placeholder?: string;
  autofocus?: boolean | 'selectAll';
  onChange?: (value: string) => void;
}

export interface SettingsPanelActionItem extends SettingsPanelItemBase {
  type: 'action';
  action?: () => void;
}

export interface SettingsPanelSelectItem extends SettingsPanelItemBase {
  type: 'select';
  commands: ICommand[];
  activeCommandId?: string;
  onSelect?: (command: ICommand) => void;
}

export interface SettingsPanelTabsItem extends SettingsPanelItemBase {
  type: 'tabs';
  commands: ICommand[];
  activeCommandId?: string;
  tabType?: string;
  onSelect?: (command: ICommand) => void;
}

export interface SettingsPanelEntityPickerItem extends SettingsPanelItemBase {
  type: 'entityPicker';
  entity: ActivityEntityName | ActivityEntityName[];
  mode: 'single' | 'multi';
  value?: string;
  count?: number;
  selected?: SettingsPanelOptionId | SettingsPanelOptionId[];
  entityOptions?: Record<string, unknown>;
  groupLabel?: (entity: ActivityEntityName) => string;
}

export interface SettingsPanelIconPickerItem extends SettingsPanelItemBase {
  type: 'iconPicker';
  value?: string;
  placeholder?: string;
}

export interface SettingsPanelTonePickerItem extends SettingsPanelItemBase {
  type: 'tonePicker';
  value?: SettingsPanelTone;
  tones?: SettingsPanelTone[];
}

export interface SettingsPanelCompoundItem {
  type: 'compound';
  label: string;
  icon?: string;
  hidden?: SettingsPanelVisibility;
  disabled?: SettingsPanelVisibility;
  items: SettingsPanelMenuItem[];
}

export interface SettingsPanelInlineComponentItem extends SettingsPanelItemBase {
  type: 'inlineComponent';
  component: Type<unknown>;
  inputs?: Record<string, unknown>;
}

export interface SettingsPanelDividerItem {
  type: 'divider';
  id?: string;
  hidden?: SettingsPanelVisibility;
}

export interface SettingsPanelGroupItem {
  type: 'group';
  id?: string;
  label: string;
  hidden?: SettingsPanelVisibility;
}

export interface SettingsPanelSelectOption<T = unknown> {
  id: string | number;
  label: string;
  icon?: string;
  avatar?: AvatarToken;
  color?: string;
  description?: string;
  group?: string;
  data?: T;
}

// ── Split button config ─────────────────────────────────────────────

export interface SettingsPanelSplitButton {
  command: Command;
  commands: Command[];
}

// ── Menu search config ──────────────────────────────────────────────

export interface SettingsPanelMenuSearch {
  enabled: boolean;
  placeholder?: string;
  autofocus?: boolean;
}

// ── Config (output of builder) ──────────────────────────────────────

export interface SettingsPanelConfig {
  title: string;
  items: SettingsPanelMenuItem[];
  search?: SettingsPanelMenuSearch;
  headerCommands?: ICommand[];
  footerLeftCommands?: ICommand[];
  footerRightCommands?: ICommand[];
  footerSplitButton?: SettingsPanelSplitButton;
  registry: Map<string, SettingsPanelEntry>;
}

// ── Navigation ──────────────────────────────────────────────────────

export interface SettingsPanelNavigateOptions extends SettingsPanelEntryBase {
  title?: string;
  providers?: Provider[];
}

export interface SettingsPanelEntryBase {
  control?: FormControl;
  headerCommands?: ICommand[];
  footerLeftCommands?: ICommand[];
  footerRightCommands?: ICommand[];
  footerSplitButton?: SettingsPanelSplitButton;
}

export interface SettingsPanelTemplateEntry extends SettingsPanelEntryBase {
  type: 'template';
  template: TemplateRef<unknown>;
}

export interface SettingsPanelComponentEntry extends SettingsPanelEntryBase {
  type: 'component';
  component: Type<unknown>;
  providers?: Provider[];
  inputs?: Record<string, unknown>;
}

export interface SettingsPanelMenuEntry extends SettingsPanelEntryBase {
  type: 'menu';
  items: SettingsPanelMenuItem[];
  search?: SettingsPanelMenuSearch;
}

export interface SettingsPanelPickerEntry extends SettingsPanelEntryBase {
  type: 'picker';
  picker: SettingsPanelPickerConfig;
  viewConfig?: Record<string, unknown>;
}

export interface SettingsPanelEntityPickerEntry extends SettingsPanelEntryBase {
  type: 'entityPicker';
  entity: ActivityEntityName | ActivityEntityName[];
  mode: 'single' | 'multi';
  entityOptions?: Record<string, unknown>;
  groupLabel?: (entity: ActivityEntityName) => string;
  viewConfig?: Record<string, unknown>;
}

export type SettingsPanelEntry = SettingsPanelTemplateEntry | SettingsPanelComponentEntry | SettingsPanelMenuEntry | SettingsPanelPickerEntry | SettingsPanelEntityPickerEntry;

export abstract class SettingsPanelNavigationService {

  public abstract navigateTo(panelId: string, options?: SettingsPanelNavigateOptions): void;

  public abstract navigateBack(): void;

  public abstract navigateToRoot(): void;
}

// ── Events ──────────────────────────────────────────────────────────

export class SettingsPanelDismissEvent {

  private _defaultPrevented = false;

  public preventDefault(): void {

    this._defaultPrevented = true;
  }

  public get defaultPrevented(): boolean {

    return this._defaultPrevented;
  }
}
